/**
 * The sign-in area at the bottom of the sidebar.
 *
 * This is its own file because of one trap worth knowing about:
 *
 * Clerk's `<SignedIn>` and `<SignedOut>` render *nothing at all* until Clerk has
 * finished loading. If the key is wrong for the current domain, Clerk never
 * loads, and the buttons silently vanish with no error on screen — it looks like
 * the feature was never built.
 *
 * A production Clerk key (`pk_live_…`) is locked to its own domain, so it always
 * fails on localhost. We therefore check `isLoaded` ourselves and say what is
 * wrong instead of showing empty space.
 */

import {
  SignInButton,
  SignUpButton,
  UserButton,
  useAuth,
} from "@clerk/clerk-react";
import { useEffect, useState } from "react";

export function SidebarAuth() {
  const { isLoaded, isSignedIn } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (isLoaded) return;
    const timer = window.setTimeout(() => setTimedOut(true), 4000);
    return () => window.clearTimeout(timer);
  }, [isLoaded]);

  if (!isLoaded) {
    return (
      <div className="flex flex-col gap-2">
        <div className="h-9 animate-pulse rounded-lg bg-slate-100" />
        <div className="h-9 animate-pulse rounded-lg bg-slate-100" />
        {timedOut && (
          <p className="rounded-lg bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-800">
            Sign-in is still starting. Use{" "}
            <a className="underline" href="https://coneproposalsoftware.com/data">
              coneproposalsoftware.com
            </a>{" "}
            if you opened a preview URL.
          </p>
        )}
      </div>
    );
  }

  if (isSignedIn) {
    return (
      <div className="flex items-center gap-2.5">
        <UserButton afterSignOutUrl="/app/funding" />
        <span className="text-sm text-slate-600">Your account</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <SignInButton mode="modal">
        <button className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50">
          Log in
        </button>
      </SignInButton>
      <SignUpButton mode="modal">
        <button className="w-full rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark">
          Sign up
        </button>
      </SignUpButton>
      <p className="px-1 pt-1 text-[11px] leading-snug text-slate-500">
        Signing in unlocks search, filters and every page of data.
      </p>
    </div>
  );
}
